import {
  ArrowCounterClockwise,
  FloppyDisk,
  GitBranch,
  PencilSimple,
} from "@phosphor-icons/react";
import { useMemo } from "react";
import { MODERATOR_SUGGESTIONS_KEY } from "../../shared/promptContracts/registry.ts";
import {
  candidateCountConflict,
  missingRequiredPromptAdditions,
} from "../../shared/suggestions/promptTransparency.ts";
import { usePromptAdminContext } from "./PromptAdminShell.tsx";

export function PromptAdminPage() {
  const {
    selectedTemplate,
    selectedDraft,
    selectedDirty,
    pending,
    setDraft,
    save,
    reset,
  } = usePromptAdminContext();
  const isSuggestionPrompt = selectedTemplate.key === MODERATOR_SUGGESTIONS_KEY;
  const candidateWarning = useMemo(
    () => isSuggestionPrompt ? candidateCountConflict(selectedDraft) : null,
    [isSuggestionPrompt, selectedDraft],
  );
  const missingAdditions = useMemo(
    () => missingRequiredPromptAdditions(selectedDraft, selectedTemplate.requiredAdditions ?? []),
    [selectedDraft, selectedTemplate.requiredAdditions],
  );
  const requestPreviews = selectedTemplate.requestPreviews ?? [];

  function appendRequiredAddition(body: string) {
    setDraft(selectedTemplate.key, `${selectedDraft.trimEnd()}\n\n${body}`);
  }

  return (
    <div data-testid="prompt-editor">
      <div className="prompt-editor-head">
        <div>
          <p className="prompt-key">{selectedTemplate.key}</p>
          <h1>{selectedTemplate.title}</h1>
          <p>{selectedTemplate.purpose}</p>
        </div>
        <div className="prompt-editor-actions">
          <span className={`prompt-state ${selectedTemplate.isCustom ? "custom" : "default"}`}>
            {selectedTemplate.isCustom ? "custom" : "repo default"}
          </span>
          <button
            className="btn warn"
            data-testid="prompt-reset"
            type="button"
            disabled={!selectedTemplate.isCustom || Boolean(pending)}
            onClick={() => void reset(selectedTemplate)}
          >
            <ArrowCounterClockwise aria-hidden="true" weight="bold" />
            {pending?.key === selectedTemplate.key && pending.type === "reset" ? "Resetting..." : "Reset"}
          </button>
          <button
            className="btn primary"
            data-testid="prompt-save"
            type="button"
            disabled={!selectedDirty || Boolean(pending)}
            onClick={() => void save(selectedTemplate)}
          >
            <FloppyDisk aria-hidden="true" weight="bold" />
            {pending?.key === selectedTemplate.key && pending.type === "save" ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <label className="prompt-editor-field">
        <span>
          <PencilSimple aria-hidden="true" weight="bold" />
          {selectedTemplate.editableLabel}
        </span>
        <textarea
          data-testid="prompt-body"
          value={selectedDraft}
          rows={18}
          spellCheck={false}
          onChange={(event) => setDraft(selectedTemplate.key, event.target.value)}
        />
      </label>

      {(candidateWarning || missingAdditions.length > 0) && (
        <section className="prompt-contract-warning" aria-label="Prompt contract attention">
          {candidateWarning && <p data-testid="prompt-candidate-count-warning">{candidateWarning}</p>}
          {missingAdditions.map((addition) => (
            <div key={addition.id} data-testid="prompt-required-addition">
              <strong>{addition.label}</strong>
              <p>{addition.reason}</p>
              <pre>{addition.body}</pre>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => appendRequiredAddition(addition.body)}
              >
                Append to draft
              </button>
            </div>
          ))}
        </section>
      )}

      <div className="prompt-inspector-grid">
        <section className="prompt-inspector-panel" data-testid="prompt-diagram">
          <h2>
            <GitBranch aria-hidden="true" weight="bold" />
            Request structure
          </h2>
          <div className="prompt-diagram">
            {["runtime inputs", "user prompt sections", "system prompt", "structured schema", "generateObject"].map((step, index) => (
              <div className="prompt-diagram-step" key={step}>
                <span>{index + 1}</span>
                <strong>{step}</strong>
              </div>
            ))}
          </div>
          <InfoList title="Runtime inputs" items={selectedTemplate.runtimeInputs} />
          <InfoList title="Structured output" items={selectedTemplate.outputSchema} />
          <InfoPairs title="Provider settings" items={selectedTemplate.providerOptions} />
        </section>

        <section className="prompt-inspector-panel" data-testid="prompt-request-preview">
          <h2>Request preview</h2>
          <div className="prompt-section-list">
            {selectedTemplate.userPromptSections.map((section) => (
              <details key={section.label} open>
                <summary>{section.label}</summary>
                <pre>{section.template}</pre>
                {section.runtimeInputs.length > 0 && (
                  <p>Uses: {section.runtimeInputs.join(", ")}</p>
                )}
              </details>
            ))}
          </div>
          {requestPreviews.length > 0 ? (
            <div className="prompt-runtime-variants" data-testid="prompt-runtime-variants">
              {requestPreviews.map((preview, index) => (
                <details className="prompt-runtime-variant" key={preview.label} open={index === 0}>
                  <summary>
                    <strong>{preview.label}</strong>
                    <span>
                      candidateCount={preview.candidateCount} · schema max={preview.schemaMaxItems}
                    </span>
                  </summary>
                  <p>{preview.description}</p>
                  <pre className="prompt-preview">{preview.requestPreview}</pre>
                </details>
              ))}
            </div>
          ) : (
            <pre className="prompt-preview">{selectedTemplate.requestPreview}</pre>
          )}
        </section>
      </div>

      <footer className="prompt-editor-footer">
        {selectedTemplate.updatedAt
          ? `Last updated by ${selectedTemplate.updatedByRole ?? "operator"} at ${new Date(selectedTemplate.updatedAt).toLocaleString()}`
          : "Using the versioned repo default."}
      </footer>
    </div>
  );
}

function InfoList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="prompt-info-block">
      <h3>{title}</h3>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}

function InfoPairs({ title, items }: { title: string; items: Array<{ label: string; value: string }> }) {
  return (
    <div className="prompt-info-block">
      <h3>{title}</h3>
      <dl>
        {items.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
